/**
 * i18n 번역 사전 — Step 7 경량 scaffold.
 *
 * 철학: 본 단계에서는 `react-i18next` / `formatjs` 같은 라이브러리를 도입하지
 * 않는다. Record 기반 naive lookup 으로 구조만 확보하고, 실 번역 작업 및
 * 플러럴/보간은 별도 i18n Epic 에서 라이브러리 전환과 함께 채운다.
 *
 * 키 네이밍: 점 구분, 도메인 prefix (`home.*`, `nav.*`, `auth.*`, `common.*`)
 */

export const LOCALES = ['ko', 'en'] as const
export type Locale = (typeof LOCALES)[number]

type Dict = Record<string, string>

const ko: Dict = {
  'nav.datasets': 'Datasets',
  'nav.curations': 'Curations',
  'nav.models': 'Models',
  'nav.mlops': 'MLOps',
  'nav.leaderboard': 'Leaderboard',
  'nav.signIn': 'Sign In',
  'nav.search': '검색',
  'home.hero.title': '로봇 지능의 시작점 — 데이터, 모델, 평가를 한 곳에서',
  'home.hero.subtitle': '{appName}에서 데이터셋, 모델, 리더보드를 탐색하세요.',
  'home.cta.datasets': '데이터셋 둘러보기',
  'home.cta.models': '모델 둘러보기',
  'home.cta.leaderboard': '리더보드 보기',
  'home.myPage': 'My Page',
  'home.section.featuredDatasets': 'Featured Datasets',
  'home.section.featuredModels': 'Featured Models',
  'home.section.leaderboardPreview': 'Leaderboard Preview',
  'home.section.viewAll': '전체 보기 →',
  'auth.login.title': '로그인',
  'auth.login.subtitle': '로봇 지능의 시작점에 접속하세요.',
  'auth.login.hfSso': 'Hugging Face 계정으로 로그인',
  'common.home': '홈',
  'common.cancel': '취소',
  'common.submit': '제출',
  // GDEV-175 Community
  'community.tab.title': '커뮤니티',
  'community.empty.title': '아직 토론이 없습니다',
  'community.empty.cta': '첫 토론을 시작해보세요',
  'community.action.new': '새 토론',
  'community.action.close': '토론 닫기',
  'community.action.reopen': '다시 열기',
  'community.action.edit': '편집',
  'community.action.delete': '삭제',
  'community.filter.status.all': '전체',
  'community.filter.status.open': '열림',
  'community.filter.status.closed': '닫힘',
  'community.filter.sort.latest': '최신순',
  'community.filter.sort.oldest': '오래된순',
  'community.filter.sort.comments': '댓글많은순',
  'community.error.fetch': 'HuggingFace 와 통신 중 오류가 발생했습니다.',
  'community.error.create': '토론 작성에 실패했습니다. 잠시 후 다시 시도해주세요.',
  'community.attribution.info': 'HuggingFace 계정이 연동되지 않아 서비스 계정 명의로 작성됩니다.',
  'community.attribution.help': 'HF 계정 연동하기',
  'community.login.required': '댓글을 작성하려면 로그인이 필요합니다.',
  'community.comment.placeholder': '댓글을 입력하세요 (markdown 지원)',
  'community.discussion.placeholder_title': '토론 제목을 입력하세요',
  'community.discussion.placeholder_body': '본문을 markdown 으로 작성하세요',
}

const en: Dict = {
  'nav.datasets': 'Datasets',
  'nav.curations': 'Curations',
  'nav.models': 'Models',
  'nav.mlops': 'MLOps',
  'nav.leaderboard': 'Leaderboard',
  'nav.signIn': 'Sign In',
  'nav.search': 'Search',
  'home.hero.title':
    'The starting point of robot intelligence — data, models, evaluation in one place',
  'home.hero.subtitle': 'Explore datasets, models, and leaderboards on {appName}.',
  'home.cta.datasets': 'Browse Datasets',
  'home.cta.models': 'Browse Models',
  'home.cta.leaderboard': 'View Leaderboard',
  'home.myPage': 'My Page',
  'home.section.featuredDatasets': 'Featured Datasets',
  'home.section.featuredModels': 'Featured Models',
  'home.section.leaderboardPreview': 'Leaderboard Preview',
  'home.section.viewAll': 'View all →',
  'auth.login.title': 'Sign In',
  'auth.login.subtitle': 'Access the starting point of robot intelligence.',
  'auth.login.hfSso': 'Sign in with Hugging Face',
  'common.home': 'Home',
  'common.cancel': 'Cancel',
  'common.submit': 'Submit',
  // GDEV-175 Community
  'community.tab.title': 'Community',
  'community.empty.title': 'No discussions yet',
  'community.empty.cta': 'Start the first discussion',
  'community.action.new': 'New discussion',
  'community.action.close': 'Close discussion',
  'community.action.reopen': 'Reopen',
  'community.action.edit': 'Edit',
  'community.action.delete': 'Delete',
  'community.filter.status.all': 'All',
  'community.filter.status.open': 'Open',
  'community.filter.status.closed': 'Closed',
  'community.filter.sort.latest': 'Newest',
  'community.filter.sort.oldest': 'Oldest',
  'community.filter.sort.comments': 'Most commented',
  'community.error.fetch': 'Failed to communicate with HuggingFace.',
  'community.error.create': 'Failed to create discussion. Please try again later.',
  'community.attribution.info':
    'Your HF account is not linked. Post will be created under the service account.',
  'community.attribution.help': 'Link HF account',
  'community.login.required': 'Sign in is required to comment.',
  'community.comment.placeholder': 'Write a comment (markdown supported)',
  'community.discussion.placeholder_title': 'Enter discussion title',
  'community.discussion.placeholder_body': 'Write the body in markdown',
}

export const DICTIONARIES: Record<Locale, Dict> = { ko, en }

/**
 * 번역 + 변수 보간.
 *
 * `t('home.hero.subtitle', 'ko', { appName: 'ETRI RobotAI' })`
 *   → "ETRI RobotAI에서 데이터셋, 모델, 리더보드를 탐색하세요."
 *
 * 키 미존재 시 해당 키 자체를 반환 (디버깅 보조).
 */
export function translate(key: string, locale: Locale, vars?: Record<string, string>): string {
  const value = DICTIONARIES[locale]?.[key] ?? key
  if (!vars) return value
  return value.replace(/\{(\w+)\}/g, (_, name) => vars[name] ?? `{${name}}`)
}
